import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetSubscriptionPlans } from '@/entities/subscription';
import { useAuth } from '@/shared/auth';
import { toError } from '@/shared/lib/toError';
import { PlanHeroCard } from '../plan-status';
import { BillingHistoryTable } from '../billing-history';
import { UsageSection, useFreeUsage } from '../usage';
import { UpgradePlanCard } from '../upgrade-plan';
import { useCreateCheckout } from '../create-checkout';
import { useCreatePaymentMethodPortalSession } from '../add-payment-method';
import { useReconcileFree } from '../reconcile-free';
import { findPlan } from '../model/findPlan';
import { getUpgradeFeatures } from '../model/getUpgradeFeatures';
import { BillingIntervalToggle, type BillingInterval } from './BillingIntervalToggle';

type Props = {
  onCheckoutError: (error: Error) => void;
  onPortalError: (error: Error) => void;
};

export const FreeTierLayout = ({ onCheckoutError, onPortalError }: Props) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const [interval, setInterval] = useState<BillingInterval>('year');

  const { data: plansData } = useGetSubscriptionPlans();
  const { data: usageData } = useFreeUsage();
  const checkoutMutation = useCreateCheckout();
  const paymentMethodMutation = useCreatePaymentMethodPortalSession();
  const reconcileMutation = useReconcileFree();

  // Best-effort: retry Free subscription provisioning if it failed during shop creation.
  // When Stripe was down or env vars were missing at signup, stripeCustomerId stays null
  // and the "Add payment method" / "Manage billing" buttons can't work. Silent on failure.
  const reconcileAttemptedRef = useRef(false);
  const reconcileMutate = reconcileMutation.mutate;
  const needsReconcile =
    sessionUser?.subscription.tier === 'free' && sessionUser?.subscription.stripeCustomerId === null;

  useEffect(() => {
    if (!needsReconcile || reconcileAttemptedRef.current) {
      return;
    }
    reconcileAttemptedRef.current = true;
    reconcileMutate(undefined, {
      onError: () => {
        // Silent — best effort. User degrades to the same state as before (no visible error).
      },
    });
  }, [needsReconcile, reconcileMutate]);

  const plans = plansData?.plans ?? [];
  const freePlan = findPlan(plans, 'free');
  const proPlan = findPlan(plans, 'pro');
  const elitePlan = findPlan(plans, 'elite');

  const handleAddPaymentMethod = () => {
    paymentMethodMutation.mutate(
      { data: { flow: 'payment_method_update' } },
      {
        onSuccess: (data) => {
          window.open(data.portalUrl, '_blank');
        },
        onError: (error) => {
          onPortalError(toError(error));
        },
      }
    );
  };

  const handleUpgrade = (tier: 'pro' | 'elite') => {
    checkoutMutation.mutate(
      { data: { tier, interval } },
      {
        onSuccess: (data) => {
          window.location.href = data.checkoutUrl;
        },
        onError: (error) => {
          onCheckoutError(toError(error));
        },
      }
    );
  };

  const handleProUpgrade = () => {
    handleUpgrade('pro');
  };

  const handleEliteUpgrade = () => {
    handleUpgrade('elite');
  };

  const promoCopyKey = interval === 'year' ? 'subscription.upgrade.promoAnnual' : 'subscription.upgrade.promoMonthly';

  return (
    <Stack spacing={3} sx={{ maxWidth: 1000 }}>
      <PlanHeroCard
        tier="free"
        interval={null}
        nextBillingDate={null}
        features={[]}
        meteredRate={freePlan?.metered?.unitAmount ?? 3}
        freeAllowance={usageData?.allowance ?? 3}
        freeRosUnlimited={usageData?.unlimited ?? false}
        onAddPaymentMethod={handleAddPaymentMethod}
        addPaymentMethodLoading={paymentMethodMutation.isPending}
      />

      <UsageSection onPortalError={onPortalError} />

      <BillingHistoryTable emptyState="free" />

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6">{t('subscription.upgrade.titleFromFree')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subscription.upgrade.subtitleFromFree')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
          <BillingIntervalToggle value={interval} onChange={setInterval} />
          <Typography variant="body2" sx={{ color: interval === 'year' ? 'success.main' : 'text.secondary' }}>
            {t(promoCopyKey)}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {proPlan && (
            <UpgradePlanCard
              plan={proPlan}
              interval={interval}
              previousTierLabel="Free"
              audienceTagline={t('subscription.upgrade.forGrowingShops')}
              features={getUpgradeFeatures('pro')}
              variant="from-free"
              ctaLabel="subscription.upgrade.upgradeToPro"
              onUpgrade={handleProUpgrade}
              upgradeLoading={checkoutMutation.isPending}
            />
          )}
          {elitePlan && (
            <UpgradePlanCard
              plan={elitePlan}
              interval={interval}
              previousTierLabel="Free"
              audienceTagline={t('subscription.upgrade.forMultiLocation')}
              features={getUpgradeFeatures('elite')}
              variant="from-free"
              ctaLabel="subscription.upgrade.upgradeToElite"
              onUpgrade={handleEliteUpgrade}
              upgradeLoading={checkoutMutation.isPending}
            />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          {t('subscription.upgrade.footer')}
        </Typography>
      </Box>
    </Stack>
  );
};
